import { Injectable } from '@angular/core';
import { INavigationService, LogService, IAuthorizationService } from '@pershing/pershing-core';
import { ActionMenuContext } from '../models/nxp-action-menu-context.model';
import { NxpActionMenuConfig } from '../utils/nxp-action-menu.config';

/**
 * @desc interacts with the back-end services to get the list of applicable action menus for an user
 */
@Injectable()
export class ActionMenuService {
    actionMenuList: any[];
    actionMenuFlags: string[];
    actionMenuContext: ActionMenuContext;
    actionMenuConfig: any;
    menudIdName = 'menuId';

    constructor(private logService: LogService, private navigationService: INavigationService,
                private authorizationService: IAuthorizationService) {
    }

    /*
        Function that process the menu config with entitlements and flags and returns the list of menus to display
    */
    getActionMenuList(context: ActionMenuContext, actionMenuFlags: string[], featureMenus?: any[]): any[] {
        let type: string;
        let enabledFlag = true;
        const roleName = 'role';

        /* Use the key or the context type/subtype/status to determine which menu items to display. */
        /* Key takes precedence over context as key is supposed to be used for special usecases. */
        if (context.key && context.key !== '') {
            type = context.key;
        } else {
            if (context.type && context.type !== '') {
                type = context.type;
                if (context.status && context.status.length > 0 ) {
                    type = type + '-' + context.status;
                } else if ( context.subtype && context.subtype !== '' ) {
                    type = type + '-' + context.subtype;
                }
            }
        }

        /* Merge repeat menu entries */
        const additionalMenuConfig = NxpActionMenuConfig.NXP_ACTION_MENU_DUPLICATE_MAPPING;
        if (additionalMenuConfig) {
            additionalMenuConfig.forEach((value, key) => {
                if (value && key) {
                    if (NxpActionMenuConfig.NXP_ACTION_MENU_CONFIG[value]) {
                        NxpActionMenuConfig.NXP_ACTION_MENU_CONFIG[key] = NxpActionMenuConfig.NXP_ACTION_MENU_CONFIG[value];
                    }
                }
            });
        }

        this.actionMenuList = []; // Menu items to display for the give inputs
        let menus: any[];
        menus = NxpActionMenuConfig.NXP_ACTION_MENU_CONFIG[type]; // All the menus from the config

        /*
        For Securities, there are multiple asset classes and except 4, others will only have one item in the action menu.
        For them instead of defining their own menu, it would be better to have one common menu.
        So check for <type>-common if a menu is not available for the specific assettype.
        */
        if ( !menus || menus.length === 0 ) {
            let commonType: string = context.type;
            if (commonType && commonType.length > 0) {
                commonType = commonType.concat('-common');
                menus = NxpActionMenuConfig.NXP_ACTION_MENU_CONFIG[commonType];
            }
        }

        let menuListIndex = 0;
        if (menus && menus.length > 0) {
            for (let i = 0; i < menus.length; i++) {
                enabledFlag = true;
                const menuCandidate: any = NxpActionMenuConfig.NXP_ACTION_MENU_MASTER[menus[i]];

                /* Check if the meni ID listed in menu is available in the menu item definition and proceed */
                if (menuCandidate) {
                    if (menuCandidate.disabled) {
                        enabledFlag = false;
                        /*
                        There can be cases where the feature menus may need to be added among other menus instead of top.
                        For these cases, we define the menu with disabled state and add them to menu list.
                        The feature team can pass an include flag as in <menuId>-include in the ActionMenuFlags based on which
                        the menus will be enabled.
                        */
                        if (actionMenuFlags && actionMenuFlags.length > 0) {
                            const menuId = menuCandidate[this.menudIdName];
                            const flag = menuCandidate.flag ? menuCandidate.flag : menuId.concat('-include');
                            const isFound = actionMenuFlags.indexOf(flag);
                            if (isFound !== -1) {
                                enabledFlag = true;
                            }
                        }
                    } else {
                        /*
                            The component can supply a list of flags to hide certain menus that are not applicable
                            either for the component or for a specific usecase.
                            The flag name could either be configured explicitly or the menu item's ID suffixed with "-exclude" implicitly.
                            For examaple, if you do not want to have Balances, then pass the flag as balances-flag. Preferred is implicit.
                            The flags can also be used for other behavior tweaks later where the menu behaviors can be modified based on
                            additional flags that are not tied to the menu names.
                        */
                        if (actionMenuFlags && actionMenuFlags.length > 0) {
                            /* First check if any menu items need to be excluded */
                            const menuId = menuCandidate[this.menudIdName];
                            const flag = menuCandidate.flag ? menuCandidate.flag : menuId.concat('-exclude');
                            // const isFound = actionMenuFlags.findIndex(element => element === flag); // Removed due to IE compatibility
                            const isFound = actionMenuFlags.indexOf(flag);
                            if (isFound !== -1) {
                                enabledFlag = false;
                            }
                        }
                    }

                    /* Check if the user is entitled to the role */
                    if (menuCandidate.role && menuCandidate.role !== '') {
                        const role = menuCandidate[roleName];
                        if (!this.authorizationService.isEntitled(role)) {
                            enabledFlag = false;
                        }
                    }

                    if (enabledFlag) {
                        this.actionMenuList[menuListIndex] = menuCandidate;
                        menuListIndex++;
                        if (!menuCandidate.isDivider) {
                            menuCandidate.isDivider = false;
                        }
                    }
                } else {
                    this.logService.error('Action Menu: Menu is not defined ' + menus[i]);
                }
            }
        } else {
            this.logService.error('Action Menu: No menu item is defined for the context info passed. Review your configuration/data.',
                                 type);
        }

        this.actionMenuList = this.updateDividers(this.actionMenuList);
        return this.actionMenuList;
    }

    /*
        Removed unnecessary dividers.
        After processing the menu config with entitlements and flags, dividers may be inappropriately placed - at top or bottom, consecutive
        This function removes such invalid Dividers
    */
    updateDividers(actionMenus: any []): any {
        if (actionMenus.length > 0) {
            /* Delete duplicate, consecutive dividers */
            if (actionMenus.length > 1) {
                for (let i = 1; i < actionMenus.length; i++) {
                    if (actionMenus[i - 1] && actionMenus[i - 1].isDivider && 
                        actionMenus[i] && actionMenus[i].isDivider) {
                        actionMenus.splice(i, 1);
                    }
                }
            }
            /* Delete if the first menu is a divider */
            if (actionMenus[0] && actionMenus[0].isDivider) {
                actionMenus.splice(0, 1);
            }
            /* Delete if the last menu is a divider */
            const lastIndex = actionMenus.length - 1;
            if (actionMenus[lastIndex] && actionMenus[lastIndex].isDivider) {
                actionMenus.splice(lastIndex, 1);
            }
        }

        return actionMenus;
    }

    /*  Below function is not needed except if we display the context value
        on the menu and allow the user to click it */
    handleContextClick(type: string, value: any): any {
        /* There are cases where the action menu will need to pass back and event to the component
            instead of invoking navigation. This also need to be handled. */
        this.navigationService.navigateOnContext(type, value);  // Publish an event instead
    }

    handleActionMenuClick(type: string, value: any, target: string): any {
        // this.logService.info('Action Menu click handler : ', type, value, target);
        this.navigationService.navigateOnAction(type, value, target); // Publish an event instead
    }

}
